import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Mail, Search } from 'lucide-react';

export default function WaitlistPage() {
  const { api } = useAuth();
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(() => {
    api.get('/admin/waitlist')
      .then(({ data: d }) => setData(d))
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = search
    ? data.items.filter((i) => i.email.toLowerCase().includes(search.toLowerCase()))
    : data.items;

  return (
    <AdminLayout>
      <div data-testid="admin-waitlist">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-medium" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Waitlist</h1>
            <p className="text-sm text-gray-500">{data.total} people waiting for updates</p>
          </div>
        </div>

        <div className="mb-5">
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#C4A484]"
              data-testid="waitlist-search"
            />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#C4A484] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm">
              {search ? 'No matching entries' : 'No waitlist entries yet'}
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="waitlist-table">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-gray-400 font-medium">Email</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-gray-400 font-medium">Source</th>
                  <th className="text-left px-5 py-3 text-xs uppercase tracking-wider text-gray-400 font-medium">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((item) => (
                  <tr key={item.id || item.email} className="hover:bg-gray-50/50 transition-colors" data-testid={`waitlist-row-${item.email}`}>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-300" />
                        <span className="text-gray-900">{item.email}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-xs bg-purple-50 text-purple-600 px-2.5 py-1 rounded-full capitalize">{(item.source || '').replace('_', ' ')}</span>
                    </td>
                    <td className="px-5 py-3.5 text-xs text-gray-400">
                      {new Date(item.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
